import { Grid2, MenuItem, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useMemo, useState, useContext } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseTextField from './ExpenseTextField';
import StatusOption from './StatusOption';
import {
    ExpenseStatusEnum,
    ExpenseStatusValuesEnum,
    ExpenseAdminStatusValuesEnum,
} from './ExpenseStatus.enum';
import { AdminAuthContext } from '../../../Admin/Contexts/AdminAuthContext';
import { ExpenseModeEnum } from './ExpenseMode.enum';

const useStyles = makeStyles(() => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
}));

const InternalNotesForm = (props) => {
    const {
        index,
        expense,
        mode,
        statusesAllowed,
        originalExpenseEntries,
        isViewOnly,
        numTabs,
        isReimbursement = false,
        isCorrection,
    } = props;
    const classes = useStyles();
    const { control, errors, watch } = useFormContext();
    const adminAuth = useContext(AdminAuthContext);
    const isExpenseAdminUser =
        !adminAuth?.isExpenseSystemAdministratorUser && adminAuth?.isExpenseAdminUser;

    const statuses = useMemo(() => {
        if (mode !== ExpenseModeEnum.PendingCreditCard) {
            let statusExp = null;
            if (originalExpenseEntries)
                statusExp = originalExpenseEntries.find((entry) => entry.id === expense?.id);

            if (isCorrection && statusExp?.status !== ExpenseStatusValuesEnum.InCorrection) {
                return [
                    {
                        key: ExpenseStatusValuesEnum.InCorrection,
                        value: ExpenseStatusValuesEnum.InCorrection,
                        UI: ExpenseStatusEnum[28].UI,
                    },
                ];
            }

            if (!statusExp) statusExp = expense;

            const currentStatus = statusExp?.status ?? ExpenseStatusValuesEnum.New;
            const allowedList =
                statusesAllowed && statusesAllowed[currentStatus]
                    ? statusesAllowed[currentStatus]
                    : [ExpenseStatusValuesEnum.New];

            // Only filter out "InCorrection" if currentStatus is "Approved" or "Posted"
            const filteredAllowedList =
                currentStatus === ExpenseStatusValuesEnum.Approved ||
                currentStatus === ExpenseStatusValuesEnum.Posted ||
                currentStatus === ExpenseStatusValuesEnum.Invoiced
                    ? allowedList.filter(
                          (status) =>
                              status !== ExpenseStatusValuesEnum.InCorrection &&
                              status !== ExpenseStatusValuesEnum.InCorrection
                      )
                    : allowedList;

            let filtered = Object.entries(ExpenseStatusEnum)
                .filter(([key, value]) => filteredAllowedList.includes(value.value))
                .map(([key, value]) => value);

            // Expense Admin Status Filtering
            if (isExpenseAdminUser) {
                // Filter to ONLY the Allowed ExpenseAdmin Status List plus the current status
                filtered = filtered.filter(
                    (status) =>
                        Object.values(ExpenseAdminStatusValuesEnum).includes(status.value) ||
                        status.value === statusExp?.status
                );
            }

            if (numTabs > 1) {
                filtered = filtered.filter(
                    (status) => status.value !== ExpenseStatusValuesEnum.NeedMoreInformation
                );
            }
            return filtered;
        }
    }, [statusesAllowed, expense, isCorrection]);

    const [curStatus, setCurStatus] = useState(expense.status);
    // Get the original status from the originalExpenseEntries
    const originalStatus = useMemo(() => {
        if (expense.id && originalExpenseEntries?.length > 0) {
            const originalExpense = originalExpenseEntries.find((entry) => entry.id === expense.id);
            return originalExpense ? originalExpense.status : expense.status;
        }
        return expense.status; // Fallback to current status if no original entry is found
    }, [expense.id, originalExpenseEntries, expense.status]);
    // Track the current status being edited
    const currentStatus = watch(`expenseEntries[${index}].status`);
    // Compute isPosted based on original status and current transient state
    const isPosted = useMemo(
        () =>
            originalStatus === ExpenseStatusValuesEnum.Posted ||
            (currentStatus === ExpenseStatusValuesEnum.OnHoldReimbursement &&
                originalStatus === ExpenseStatusValuesEnum.Posted),
        [originalStatus, currentStatus]
    );
    const isPendingSubmission = expense.status === ExpenseStatusValuesEnum.PendingSubmission;
    const canEditStatus = useMemo(() => {
        if (mode === ExpenseModeEnum.Payment) {
            return true;
        }
        if (isCorrection) return true;
        if (isPosted) {
            // Only allow editing Posted expenses if the user is an Expense System Administrator
            if (mode !== ExpenseModeEnum.CreditCard) {
                return adminAuth?.isExpenseSystemAdministratorUser;
            }
            return !isViewOnly;
        }
        return (
            !isPendingSubmission &&
            (adminAuth?.isExpenseSystemAdministratorUser || adminAuth?.isExpenseAdminUser)
        );
    }, [isPosted, isPendingSubmission, adminAuth, isCorrection]);

    const MAX_LENGTH = 1000;

    return (
        <div>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                Internal Notes
            </Typography>
            <Grid2 container spacing={3}>
                <Grid2 item size={{ xs: 12 }}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].notes`}
                        defaultValue={expense.notes || ''}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                multiline
                                inputProps={{ maxLength: MAX_LENGTH }}
                                minRows={3}
                                id={`notes${index}`}
                                onChange={onChange}
                                value={value}
                                disabled={
                                    isViewOnly ||
                                    (isPosted && mode !== ExpenseModeEnum.Vault && isViewOnly) ||
                                    (isPendingSubmission && mode !== ExpenseModeEnum.Payment) ||
                                    currentStatus === ExpenseStatusValuesEnum.Abandoned
                                }
                                label="LT Internal Notes"
                                helperText={value && `${value.length}/${MAX_LENGTH}`}
                                placeholder="Internal notes here"
                                slotProps={{
                                    input: {
                                        sx: {
                                            height: 'auto', // Allow auto height
                                            fontSize: '14px',
                                            padding: '10px 12px !important',
                                        },
                                    },
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        marginTop: '0',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        transform: 'translate(0, 1.5px) scale(0.75) !important',
                                    },
                                    '& .MuiInputBase-root::before': {
                                        borderBottom: 'none !important',
                                    }, // Removes default underline
                                    '& .MuiInputBase-root::after': {
                                        borderBottom: 'none !important',
                                    }, // Prevents focus underline
                                }}
                            />
                        )}
                    />
                </Grid2>
                {mode === ExpenseModeEnum.PendingCreditCard || (expense?.id ?? 0) === 0 ? null : (
                    <>
                        <Grid2 item size={{ xs: 12, md: 6 }}>
                            <Controller
                                control={control}
                                name={`expenseEntries[${index}].status`}
                                // No need for defaultValue here if you use useEffect to set it
                                render={({ onChange, value }) => (
                                    <ExpenseTextField
                                        id={`status${index}`}
                                        select
                                        onChange={onChange}
                                        value={
                                            value ||
                                            (statuses.length === 1 ? statuses[0].value : '')
                                        }
                                        label="Status"
                                        required={
                                            expense?.status !== ExpenseStatusValuesEnum.Abandoned
                                        }
                                        disabled={
                                            currentStatus === ExpenseStatusValuesEnum.Invoiced ||
                                            currentStatus === ExpenseStatusValuesEnum.Outdated ||
                                            ((isViewOnly || !canEditStatus) && !isReimbursement) ||
                                            (isReimbursement &&
                                                currentStatus ===
                                                    ExpenseStatusValuesEnum.PendingSubmission)
                                        }
                                        error={Boolean(errors.expenseEntries?.[index]?.status)}
                                        helperText={errors.expenseEntries?.[index]?.status?.message}
                                    >
                                        <MenuItem value="" disabled>
                                            Select a status
                                        </MenuItem>
                                        {statuses.map((status, idx) => (
                                            <MenuItem key={status.value} value={status.value}>
                                                <StatusOption
                                                    status={status.value}
                                                    label={status.UI}
                                                />
                                            </MenuItem>
                                        ))}
                                    </ExpenseTextField>
                                )}
                            />
                        </Grid2>
                        {currentStatus !== ExpenseStatusValuesEnum.OnHoldReimbursement ? null : (
                            <Grid2 item size={{ xs: 12 }}>
                                <Controller
                                    control={control}
                                    name={`expenseEntries[${index}].onHoldComment`}
                                    defaultValue={expense.onHoldComment || ''}
                                    rules={{
                                        required: 'On Hold reason is required',
                                    }}
                                    render={({ onChange, value }) => (
                                        <ExpenseTextField
                                            multiline
                                            inputProps={{ maxLength: 200 }}
                                            minRows={3}
                                            id={`on-hold-comment${index}`}
                                            onChange={onChange}
                                            value={value}
                                            label="On Hold Reason"
                                            placeholder="Enter the on hold reason here"
                                            required
                                            error={Boolean(
                                                errors.expenseEntries?.[index]?.onHoldComment
                                            )}
                                            helperText={
                                                errors.expenseEntries?.[index]?.onHoldComment
                                                    ?.message
                                            }
                                        />
                                    )}
                                />
                            </Grid2>
                        )}
                    </>
                )}
            </Grid2>
        </div>
    );
};

export default InternalNotesForm;
